import { NvrResponse, RemoteLoginResult } from '../structure/remote';
import { XmlHandler } from '../utils/XmlHandler';
import { Base, BaseConfig } from './Base';
import { ErrorHandler } from '../utils/ErrorHandler';
import { ApiError } from '../errors/HikvisionError';
import { Logger, ConsoleLogger } from '../interfaces/Logger';
import { HttpClient, AxiosHttpClient } from '../interfaces/HttpClient';
import { ConfigValidator, DefaultConfigValidator } from '../interfaces/ConfigValidator';
import axios from 'axios';

export class Camera extends Base {
  protected readonly wsPort: number = 7681;

  public get url(): string {
    return `http://${this.user}:${this.password}@${this.ip}:${this.port}`;
  }

  protected get headers(): Record<string, string> {
    return this.useProxy
      ? {
        proxy: this.url,
        Cookie: this.version === 2 ? this.session : `WebSession=${this.session}`
      }
      : {
        Cookie: this.version === 2 ? this.session : `WebSession=${this.session}`
      };
  }

  constructor(
    config: BaseConfig,
    httpClient?: HttpClient,
    logger?: Logger,
    configValidator?: ConfigValidator
  ) {
    // Create default instances if not provided
    const axiosInstance = axios.create({
      baseURL: config.proxy ?? `http://${config.ip}:${config.port ?? 80}`,
      timeout: config.timeout ?? 10000
    });
    
    super(
      config,
      httpClient ?? new AxiosHttpClient(axiosInstance),
      logger ?? new ConsoleLogger(),
      configValidator ?? new DefaultConfigValidator()
    );
  }

  public async connect(): Promise<void> {
    if (this.version === 2) {
      const params = await this.getSessionParams();
      const p = this.encodePassword(params);
      const response = await this.httpClient.post(`/ISAPI/Security/sessionLogin?timeStamp=${Date.now()}`,
        XmlHandler.build({
          SessionLogin: {
            userName: this.user,
            password: p,
            sessionID: params.sessionId,
            isSessionIDValidLongTerm: false,
            sessionIDVersion: 2
          }
        }),
        {
          headers: this.headers
        });
      const json = await XmlHandler.parser<RemoteLoginResult>(response.data);
      const wrap = json.SessionUserCheck ?? json.SessionLogin;
      this.session = wrap?.sessionID ?? '';
    } else {
      await this.httpClient.get(`/ISAPI/Security/userCheck?timeStamp=${Date.now()}`);
    }
  }

  public async direction(horizontal: number, vertical: number): Promise<void> {
    await this.httpClient.put('/ISAPI/PTZCtrl/channels/1/continuous',
      XmlHandler.build({
        PTZData: {
          pan: horizontal,
          tilt: vertical
        }
      }), {
        headers: this.headers
      });
  }

  public async zoom(param: number): Promise<void> {
    await this.httpClient.put('ISAPI/PTZCtrl/channels/1/continuous',
      XmlHandler.build({
        PTZData: {
          zoom: param
        }
      }), {
        headers: this.headers
      });
  }

  public async iris(channelId: number, param: number): Promise<void> {
    await this.httpClient.put('/ISAPI/System/Video/inputs/channels/1/iris',
      XmlHandler.build({
        IrisData: {
          iris: param
        }
      }), {
        headers: this.headers
      });
  }

  public async focus(param: number): Promise<void> {
    await this.httpClient.put('/ISAPI/System/Video/inputs/channels/1/focus',
      XmlHandler.build({
        FocusData: {
          focus: param
        }
      }), {
        headers: this.headers
      });
  }

  public async light(enable: boolean): Promise<void> {
    return ErrorHandler.withErrorHandling(async () => {
      let result: string;
      try {
        const response = await this.httpClient.put('/ISAPI/PTZCtrl/channels/1/auxcontrols/1',
          XmlHandler.build({
            PTZAux: {
              id: '1',
              type: 'LIGHT',
              status: enable ? 'on' : 'off'
            }
          }),
          {
            headers: this.headers
          });
        result = response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          result = error.response.data;
        } else {
          throw error;
        }
      }
      const r = await XmlHandler.parser<NvrResponse>(result);
      if (r.ResponseStatus.statusCode !== '1') {
        throw new ApiError(
          r.ResponseStatus.subStatusCode || 'Failed to control light',
          undefined,
          r.ResponseStatus.subStatusCode
        );
      }
    }, 'light');
  }

  public async wiper(enable: boolean): Promise<void> {
    return ErrorHandler.withErrorHandling(async () => {
      let result: string;
      try {
        const response = await this.httpClient.put('/ISAPI/PTZCtrl/channels/1/auxcontrols/1',
          XmlHandler.build({
            PTZAux: {
              id: '1',
              type: 'WIPER',
              status: enable ? 'on' : 'off'
            }
          }),
          {
            headers: this.headers
          });
        result = response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          result = error.response.data;
        } else {
          throw error;
        }
      }
      const r = await XmlHandler.parser<NvrResponse>(result);
      if (r.ResponseStatus.statusCode !== '1') {
        throw new ApiError(
          r.ResponseStatus.subStatusCode || 'Failed to control wiper',
          undefined,
          r.ResponseStatus.subStatusCode
        );
      }
    }, 'wiper');
  }
}
